import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import {
  getDefaultPrintLocation,
  listPrintLocations,
  type PrintLocation,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { documentPaperKey, storedPaperSummary } from "@/lib/documentPaperOverrides";
import { startMontagePackPrint } from "@/projectOverview/montageunterlagenPrint";
import type { DocumentPackKind, MontagePackTab } from "@/projectOverview/montageunterlagenTabs";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export function MontageunterlagenPrintDialog({
  open,
  onClose,
  orderId,
  tabs,
  pack = "montage",
  zIndexClass = PROJECT_OVERVIEW_NESTED_OVERLAY_Z,
}: {
  open: boolean;
  onClose: () => void;
  orderId: number;
  tabs: MontagePackTab[];
  pack?: DocumentPackKind;
  zIndexClass?: string;
}) {
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [copies, setCopies] = useState("1");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const tabKeys = useMemo(() => tabs.map((tab) => tab.key), [tabs]);

  useEffect(() => {
    if (!open) return;
    setSelectedKeys(new Set(tabs.map((tab) => tab.key)));
    setCopies("1");
    setError(null);
    setLoading(true);
    void Promise.all([listPrintLocations(), getDefaultPrintLocation().catch(() => null)])
      .then(([list, def]) => {
        setLocations(list);
        const preferred = def?.default_location_id;
        if (preferred != null && list.some((loc) => loc.id === preferred)) {
          setLocationId(preferred);
        } else if (list[0]?.id) {
          setLocationId(list[0].id);
        } else {
          setLocationId(null);
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Druckorte konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [open, tabs]);

  const allSelected = tabs.length > 0 && selectedKeys.size === tabs.length;
  const noneSelected = selectedKeys.size === 0;

  function toggleKey(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectAll() {
    setSelectedKeys(new Set(tabKeys));
  }

  function clearAll() {
    setSelectedKeys(new Set());
  }

  function startPrint(selectedTabs: MontagePackTab[]) {
    if (locationId == null) {
      setError("Bitte einen Druckort wählen.");
      return;
    }
    const n = parseInt(copies, 10);
    if (!Number.isFinite(n) || n < 1) {
      setError("Bitte eine gültige Kopienanzahl eingeben.");
      return;
    }
    if (!selectedTabs.length) {
      setError("Bitte mindestens ein Dokument auswählen.");
      return;
    }
    // Modal sofort schließen – Druck läuft im Hintergrund mit Toast-Feedback.
    onClose();
    startMontagePackPrint(orderId, selectedTabs, locationId, n, pack);
  }

  function handlePrintSelected() {
    startPrint(tabs.filter((tab) => selectedKeys.has(tab.key)));
  }

  function handlePrintAll() {
    startPrint(tabs);
  }

  const packTitle =
    pack === "werkstatt"
      ? "Werkstattunterlagen"
      : pack === "aufmass"
        ? "Aufmaßunterlagen"
        : "Montageunterlagen";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${packTitle} drucken`}
      description="Die Auswahl wird als ein Druckauftrag gesendet und bleibt in der Warteschlange zusammen. A3-Seiten folgen bei Bedarf als eigener Auftrag direkt dahinter."
      size="md"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handlePrintAll}
              disabled={loading || locationId == null || tabs.length === 0}
            >
              Alles drucken
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handlePrintSelected}
              disabled={loading || locationId == null || noneSelected}
            >
              Drucken
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          {error ? <p className="text-sm text-status-red">{error}</p> : null}

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                Dokumente
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs font-medium text-accent hover:underline"
                  onClick={selectAll}
                  disabled={allSelected}
                >
                  Alle
                </button>
                <button
                  type="button"
                  className="text-xs font-medium text-accent hover:underline"
                  onClick={clearAll}
                  disabled={noneSelected}
                >
                  Keine
                </button>
              </div>
            </div>
            {tabs.length ? (
              <ul className="max-h-60 space-y-1.5 overflow-y-auto rounded-ios border border-separator p-2">
                {tabs.map((tab) => {
                  const paperHint =
                    storedPaperSummary(documentPaperKey(orderId, pack, tab.key)) ||
                    "Format wird beim Druck erkannt";
                  return (
                    <li key={tab.key}>
                      <Checkbox
                        id={`montage-print-${tab.key}`}
                        label={
                          <span>
                            {tab.label}
                            <span className="ml-1.5 font-normal text-label-tertiary">
                              · {paperHint}
                            </span>
                          </span>
                        }
                        checked={selectedKeys.has(tab.key)}
                        onChange={() => toggleKey(tab.key)}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-label-tertiary">Keine Dokumente vorhanden.</p>
            )}
          </div>

          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Druckort</span>
            <select
              className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm"
              value={locationId ?? ""}
              onChange={(e) => setLocationId(Number(e.target.value) || null)}
            >
              {locations.length === 0 ? <option value="">— kein Druckort —</option> : null}
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name || `Ort ${loc.id}`}
                </option>
              ))}
            </select>
          </label>

          <TextInput label="Kopien" value={copies} onChange={(e) => setCopies(e.target.value)} />
        </div>
      )}
    </Dialog>
  );
}
